/**
 * Product page elements (all progressive: forms and links work without them).
 *
 *  <product-gallery>          thumbnails, stage, prev/next, counter, swipe,
 *                             full-screen lightbox (<dialog>), variant media,
 *                             "View in your space" for 3D models (Shopify XR)
 *  <product-model>            3D model: poster until activated, then Shopify's
 *                             model viewer (loaded on demand)
 *  <gift-card-recipient>      recipient fields for gift cards (line item
 *                             properties), disabled while the toggle is off
 *  <variant-picker>           options → variant; URL, hidden ids, media and
 *                             [data-variant-swap] regions via the Section
 *                             Rendering API (?variant=…&section_id=…)
 *  <product-form>             posts the whole form (properties, selling plan,
 *                             app fields) through MatterCart (drawer)
 *  <quantity-input>           stepper; keeps the buy button total in sync
 *  <sticky-buy-bar>           shows when the main buy button leaves the view
 *  <read-more>                collapsed rich text with a toggle
 *  <product-recommendations>  loads the recommendations section lazily
 *
 * Wrapped in a guard so the file may be included by more than one section.
 */
if (!customElements.get('product-gallery')) {
  const formatMoney = (cents, format = '{{amount}}') => {
    const value = Number(cents) / 100;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    let text;
    switch (key) {
      case 'amount_no_decimals':
        text = Math.round(value).toLocaleString('en-US');
        break;
      case 'amount_with_comma_separator':
        text = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        break;
      case 'amount_no_decimals_with_comma_separator':
        text = Math.round(value).toLocaleString('de-DE');
        break;
      case 'amount_with_apostrophe_separator':
        text = value.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        break;
      default:
        text = value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, text);
  };

  const pad = (n) => String(n).padStart(2, '0');

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.current = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = this.slides.findIndex((slide) => slide.dataset.gallerySlide === thumb.dataset.galleryThumb);
          this.show(index, { scroll: true });
        });
      });
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
      this.querySelector('[data-gallery-open]')?.addEventListener('click', () => this.openLightbox());
      const stage = this.querySelector('[data-gallery-stage]');
      this.bindSwipe(stage, (delta) => this.step(delta));
      /* The photo itself opens the lightbox; controls and videos keep their own behaviour. */
      stage?.addEventListener('click', (event) => {
        if (this.dataset.zoom !== 'true' || !this.lightbox) return;
        if (event.target.closest('button, a, video, iframe, model-viewer, product-model')) return;
        const slide = event.target.closest('[data-gallery-slide]');
        if (!slide || slide.dataset.mediaType !== 'image') return;
        if (this.swiped) return;
        const index = this.slides.indexOf(slide);
        if (index >= 0 && index !== this.index) this.show(index);
        this.openLightbox();
      });
      this.bindLightbox();
      this.setupXR();
      /* The thumbnail rail fades at the bottom only while it overflows. */
      const rail = this.querySelector('.gallery__thumbs');
      if (rail && 'ResizeObserver' in window) {
        const check = () => rail.classList.toggle('is-scrollable', rail.scrollHeight > rail.clientHeight + 2);
        new ResizeObserver(check).observe(rail);
        check();
      }
    }

    get single() {
      return this.dataset.layout !== 'grid' && this.dataset.layout !== 'stacked';
    }

    step(delta) {
      if (this.slides.length < 2) return;
      this.show((this.index + delta + this.slides.length) % this.slides.length);
    }

    show(index, { scroll = false } = {}) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;
      const active = this.slides[index];
      if (this.single) {
        this.slides.forEach((slide, i) => {
          slide.hidden = i !== index;
          if (i !== index) this.pauseMedia(slide);
        });
      } else if (scroll) {
        active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.galleryThumb === active.dataset.gallerySlide;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', String(isActive));
        if (isActive && this.single) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
      if (this.current) this.current.textContent = pad(index + 1);
      /* Desktop stage follows the image ratio, clamped so the row never jumps far. */
      const ratio = Number(active.dataset.ratio);
      if (Number.isFinite(ratio) && ratio > 0) this.style.setProperty('--stage-ratio', String(Math.min(1.35, Math.max(0.8, ratio))));
      /* "View in your space" follows the 3D model on stage. */
      const xr = this.querySelector('[data-gallery-xr]');
      if (xr && active.dataset.mediaType === 'model') xr.setAttribute('data-shopify-model3d-id', active.dataset.gallerySlide);
      this.updateLightbox();
    }

    /* Only the media on stage plays: hidden videos, YouTube/Vimeo players and
     * 3D models are paused when their slide leaves. */
    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((frame) => {
        const src = frame.getAttribute('src') || '';
        if (src.includes('youtube')) frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        else if (src.includes('vimeo')) frame.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
    }

    /* Shopify XR ("View in your space"): registers the product's 3D models
     * once the feature script is ready; the button stays hidden on devices
     * without AR (data-shopify-xr-hidden). */
    setupXR() {
      const json = this.querySelector('[data-product-models]');
      if (!json || !window.Shopify?.loadFeatures) return;
      const register = (errors) => {
        if (errors) return;
        if (!window.ShopifyXR) {
          document.addEventListener('shopify_xr_initialized', () => register(), { once: true });
          return;
        }
        try {
          window.ShopifyXR.addModels(JSON.parse(json.textContent));
          window.ShopifyXR.setupXRElements();
        } catch { /* invalid model data: keep the button hidden */ }
      };
      window.Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: register }]);
    }

    showMedia(id) {
      if (!id) return;
      const index = this.slides.findIndex((slide) => slide.dataset.gallerySlide === String(id));
      if (index >= 0) this.show(index, { scroll: true });
    }

    bindSwipe(stage, onSwipe) {
      if (!stage) return;
      let startX = null;
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        /* 3D models and video controls own their gestures. */
        if (event.target.closest('model-viewer, product-model.is-loaded, video, iframe')) {
          startX = null;
          return;
        }
        startX = event.clientX;
        this.swiped = false;
      });
      stage.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) > 40) {
          this.swiped = true;
          onSwipe(delta < 0 ? 1 : -1);
          window.setTimeout(() => {
            this.swiped = false;
          }, 300);
        }
      });
    }

    bindLightbox() {
      const box = this.lightbox;
      if (!box) return;
      this.lightboxImage = box.querySelector('[data-lightbox-image]');
      this.lightboxCaption = box.querySelector('[data-lightbox-caption]');
      this.lightboxCurrent = box.querySelector('[data-lightbox-current]');
      this.lightboxStage = box.querySelector('[data-lightbox-stage]');
      this.lightboxThumbs = Array.from(box.querySelectorAll('[data-lightbox-thumb]'));
      const zoom = box.querySelector('[data-lightbox-zoom]');

      box.querySelector('[data-lightbox-close]')?.addEventListener('click', () => box.close());
      box.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.step(-1));
      box.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.step(1));
      this.lightboxThumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.show(index)));
      this.bindSwipe(this.lightboxStage, (delta) => this.step(delta));
      this.decoded = new Map();

      const toggleZoom = (event) => {
        if (!this.lightboxStage || this.swiped) return;
        const zoomed = !this.lightboxStage.classList.contains('is-zoomed');
        this.lightboxStage.classList.toggle('is-zoomed', zoomed);
        zoom?.setAttribute('aria-pressed', String(zoomed));
        if (event && zoomed && this.lightboxImage) {
          const rect = this.lightboxImage.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          this.lightboxImage.style.transformOrigin = `${x}% ${y}%`;
        }
      };
      zoom?.addEventListener('click', () => toggleZoom());
      this.lightboxImage?.addEventListener('click', (event) => toggleZoom(event));

      box.addEventListener('click', (event) => {
        if (event.target === box) box.close();
      });
      box.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });
      box.addEventListener('close', () => {
        this.lightboxStage?.classList.remove('is-zoomed');
        zoom?.setAttribute('aria-pressed', 'false');
        this.querySelector('[data-gallery-open]')?.focus({ preventScroll: true });
      });
    }

    openLightbox() {
      if (!this.lightbox) return;
      this.updateLightbox();
      this.lightbox.showModal();
    }

    /* Decode a slide's large image off-screen once; resolves when it can paint. */
    decode(slide) {
      if (!slide?.dataset.large) return Promise.resolve();
      if (!this.decoded) this.decoded = new Map();
      if (!this.decoded.has(slide)) {
        const image = new Image();
        image.sizes = '100vw';
        if (slide.dataset.largeSrcset) image.srcset = slide.dataset.largeSrcset;
        image.src = slide.dataset.large;
        this.decoded.set(slide, image.decode().catch(() => {}));
      }
      return this.decoded.get(slide);
    }

    preloadAround(index) {
      [index + 1, index - 1].forEach((i) => {
        const slide = this.slides[(i + this.slides.length) % this.slides.length];
        if (slide && slide !== this.slides[index]) this.decode(slide);
      });
    }

    async updateLightbox() {
      if (!this.lightbox) return;
      const slide = this.slides[this.index];
      if (!slide) return;
      if (this.lightboxCaption) this.lightboxCaption.textContent = slide.dataset.alt || '';
      if (this.lightboxCurrent) this.lightboxCurrent.textContent = pad(this.index + 1);
      this.lightboxThumbs.forEach((thumb, i) => {
        const on = i === this.index;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      });
      this.lightboxStage?.classList.remove('is-zoomed');
      if (!this.lightboxImage || !slide.dataset.large) return;
      const token = (this.lightboxToken = (this.lightboxToken || 0) + 1);
      const isOpen = this.lightbox.open;
      if (isOpen) this.lightboxStage?.classList.add('is-swapping');
      await this.decode(slide);
      if (token !== this.lightboxToken) return;
      this.lightboxImage.srcset = slide.dataset.largeSrcset || '';
      this.lightboxImage.src = slide.dataset.large;
      this.lightboxImage.alt = slide.dataset.alt || '';
      window.requestAnimationFrame(() => this.lightboxStage?.classList.remove('is-swapping'));
      this.preloadAround(this.index);
    }
  }

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.root = document.getElementById(this.dataset.root) || this.closest('.shopify-section') || document;
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      } catch {
        this.variants = [];
      }
      this.controller = null;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability(this.selectedValues());
    }

    groups() {
      return Array.from(this.querySelectorAll('[data-option-index]'));
    }

    selectedValues() {
      return this.groups().map((group) => {
        const radio = group.querySelector('input[type="radio"]:checked');
        if (radio) return radio.value;
        const select = group.querySelector('select');
        return select ? select.value : null;
      });
    }

    onChange() {
      /* Combined listings: a value that belongs to another product swaps the
       * whole section for that product (Shopify's combined listings app). */
      const foreign = this.groups()
        .map((group) => group.querySelector('input[type="radio"]:checked') || group.querySelector('select')?.selectedOptions[0])
        .find((element) => element?.dataset.productUrl);
      if (foreign) {
        this.switchProduct(foreign.dataset.productUrl);
        return;
      }
      const values = this.selectedValues();
      const variant = this.variants.find((candidate) => candidate.options.every((option, i) => option === values[i]));
      this.groups().forEach((group, i) => {
        const label = group.querySelector('[data-option-selected]');
        if (label) label.textContent = values[i] || '';
      });
      this.updateAvailability(values);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant || null } }));

      if (!variant) {
        this.root.querySelectorAll('[data-add-button]').forEach((button) => {
          button.disabled = true;
        });
        return;
      }

      /* Inside a quick view the picker works on another product's URL and
       * must not touch the page history. */
      const url = new URL(this.dataset.productUrl || window.location.href, window.location.origin);
      url.searchParams.set('variant', variant.id);
      if (!this.dataset.productUrl) window.history.replaceState({}, '', url);
      /* A change event lets Shopify's own form widgets (Shop Pay
       * Installments banner) follow the variant. */
      this.root.querySelectorAll('[data-variant-id-input]').forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.root.querySelector('product-gallery')?.showMedia(variant.featured_media?.id);
      this.refresh(variant, url);
    }

    async refresh(variant, url) {
      this.controller?.abort();
      this.controller = new AbortController();
      this.root.setAttribute?.('aria-busy', 'true');
      try {
        const response = await fetch(`${url.pathname}?variant=${variant.id}&section_id=${encodeURIComponent(this.sectionId)}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        this.root.querySelectorAll('[data-variant-swap]').forEach((slot) => {
          const next = doc.querySelector(`[data-variant-swap="${slot.dataset.variantSwap}"]`);
          if (!next) return;
          slot.innerHTML = next.innerHTML;
          ['hidden', 'disabled'].forEach((attr) => {
            if (next.hasAttribute(attr)) slot.setAttribute(attr, '');
            else slot.removeAttribute(attr);
          });
        });
        /* Quantity rules (min / max / increment) follow the variant. */
        this.root.querySelectorAll('[data-variant-sync][id]').forEach((field) => {
          const next = doc.getElementById(field.id);
          if (!next) return;
          field.dataset.variantSync.split(' ').forEach((attr) => {
            if (next.hasAttribute(attr)) field.setAttribute(attr, next.getAttribute(attr));
            else field.removeAttribute(attr);
          });
          const min = Number(field.min) || 1;
          if (Number(field.value) < min) field.value = min;
          field.closest('quantity-input')?.sync?.();
        });
        this.dispatchEvent(new CustomEvent('variant:swapped', { bubbles: true, detail: { variant } }));
      } catch (error) {
        if (error.name !== 'AbortError') console.warn(error);
      }
      this.root.removeAttribute?.('aria-busy');
    }

    async switchProduct(productUrl) {
      if (!(this.root instanceof Element) || !this.root.id) {
        window.location.assign(productUrl);
        return;
      }
      this.root.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${productUrl}?section_id=${encodeURIComponent(this.sectionId)}`);
        if (!response.ok) throw new Error(`Product request failed (${response.status})`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = doc.getElementById(this.root.id);
        if (!next) throw new Error('section missing');
        const root = this.root;
        root.replaceWith(next);
        if (!this.dataset.productUrl) window.history.replaceState({}, '', productUrl);
        next.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));
        next.querySelector('variant-picker')?.dispatchEvent(new CustomEvent('variant:swapped', { bubbles: true, detail: { variant: null } }));
      } catch (error) {
        console.warn(error);
        window.location.assign(productUrl);
      }
    }

    updateAvailability(values) {
      this.groups().forEach((group, index) => {
        group.querySelectorAll('[data-option-value]').forEach((element) => {
          const value = element.dataset.optionValue;
          const matches = (candidate) => candidate.options.every((option, i) => (i === index ? option === value : option === values[i]));
          const exists = this.variants.some(matches);
          const available = this.variants.some((candidate) => candidate.available && matches(candidate));
          element.classList.toggle('is-unavailable', !available);
          element.classList.toggle('is-missing', !exists);
        });
      });
    }
  }

  /* <product-tabs data-style="tabs">: on desktop the accordion blocks inside
   * become a tablist + panels; phones keep the accordions. */
  class ProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.style !== 'tabs' || !this.parentElement) return;
      this.items = Array.from(this.parentElement.querySelectorAll(':scope > .accordion'));
      if (this.items.length < 2) return;
      this.media = window.matchMedia('(min-width: 56.25rem)');
      this.apply = () => (this.media.matches ? this.enable() : this.disable());
      this.media.addEventListener('change', this.apply);
      this.apply();
    }

    disconnectedCallback() {
      this.media?.removeEventListener('change', this.apply);
    }

    enable() {
      if (this.list) return;
      this.list = document.createElement('div');
      this.list.className = 'product-tabs__list';
      this.list.setAttribute('role', 'tablist');
      this.items.forEach((item, index) => {
        if (!item.id) item.id = `ProductTab-${index}-${Math.random().toString(36).slice(2, 7)}`;
        const tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'product-tabs__tab';
        tab.id = `${item.id}-tab`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', item.id);
        tab.textContent = item.querySelector('summary')?.textContent.trim() || '';
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const count = this.items.length;
          if (event.key === 'ArrowRight') this.select((index + 1) % count, true);
          if (event.key === 'ArrowLeft') this.select((index - 1 + count) % count, true);
        });
        this.list.append(tab);
        item.setAttribute('role', 'tabpanel');
        item.setAttribute('aria-labelledby', tab.id);
      });
      this.append(this.list);
      this.hidden = false;
      this.classList.add('is-tabs');
      this.select(0);
    }

    select(index, focus = false) {
      this.items.forEach((item, i) => {
        item.hidden = i !== index;
        const details = item.querySelector('details');
        if (details && i === index) details.open = true;
      });
      this.list.querySelectorAll('[role="tab"]').forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        if (focus && i === index) tab.focus();
      });
    }

    disable() {
      if (!this.list) return;
      this.list.remove();
      this.list = null;
      this.hidden = true;
      this.classList.remove('is-tabs');
      this.items.forEach((item) => {
        item.hidden = false;
        item.removeAttribute('role');
        item.removeAttribute('aria-labelledby');
      });
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    /* The whole form goes to the cart: variant, quantity, line item
     * properties (gift card recipient, personalisation), selling plan and
     * any field an app associates with form="…". */
    async onSubmit(event) {
      if (this.dataset.cartType !== 'drawer' || !window.MatterCart?.addForm) return;
      event.preventDefault();
      const recipient = this.querySelector('gift-card-recipient');
      if (recipient && !recipient.validate()) return;
      const submitter = event.submitter || this.form.querySelector('[type="submit"]');
      if (this.error) this.error.hidden = true;
      recipient?.clearErrors();
      try {
        await window.MatterCart.addForm(this.form, submitter);
        recipient?.reset();
      } catch (error) {
        const fields = error?.data?.errors;
        if (recipient && fields && typeof fields === 'object') recipient.showErrors(fields);
        if (this.error) {
          this.error.textContent = error?.message || this.dataset.errorGeneric || '';
          this.error.hidden = false;
        }
      }
    }
  }

  /* <product-model>: a 3D model's poster until the shopper activates it,
   * then Shopify's model viewer with its UI (feature loaded on demand). */
  class ProductModel extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-model-load]')?.addEventListener('click', () => this.load());
    }

    load() {
      if (this.classList.contains('is-loaded')) return;
      const template = this.querySelector('[data-model-template]');
      if (!template) return;
      this.append(template.content.cloneNode(true));
      this.classList.add('is-loaded');
      const viewer = this.querySelector('model-viewer');
      window.Shopify?.loadFeatures?.([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !viewer || !window.Shopify.ModelViewerUI) return;
            this.ui = new window.Shopify.ModelViewerUI(viewer);
            viewer.focus?.();
          },
        },
      ]);
    }
  }

  /* <gift-card-recipient>: while the toggle is off the fields are disabled
   * (nothing is sent); on, the email becomes required, the send date is
   * limited to today … +90 days, the message counts down from 200 and the
   * shopper's timezone offset travels with the order. */
  class GiftCardRecipient extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-recipient-toggle]');
      this.fieldsBox = this.querySelector('[data-recipient-fields]');
      this.fields = Array.from(this.querySelectorAll('[data-recipient-field], [data-recipient-offset]'));
      this.email = this.querySelector('[data-recipient-field="email"]');
      this.message = this.querySelector('[data-recipient-field="message"]');
      this.count = this.querySelector('[data-recipient-count]');
      this.sendOn = this.querySelector('[data-recipient-field="send_on"]');
      if (!this.toggle || !this.fieldsBox) return;
      const offset = this.querySelector('[data-recipient-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
      if (this.sendOn) {
        const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const today = new Date();
        const last = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
        this.sendOn.min = iso(today);
        this.sendOn.max = iso(last);
      }
      this.toggle.addEventListener('change', () => this.sync(true));
      this.message?.addEventListener('input', () => this.updateCount());
      this.classList.add('is-enhanced');
      this.sync(false);
      this.updateCount();
    }

    sync(focus) {
      const on = this.toggle.checked;
      this.fieldsBox.hidden = !on;
      this.fields.forEach((field) => {
        field.disabled = !on;
      });
      if (this.email) this.email.required = on;
      if (on && focus) this.email?.focus();
    }

    updateCount() {
      if (!this.count || !this.message) return;
      const left = Math.max(0, 200 - this.message.value.length);
      this.count.textContent = (this.count.dataset.template || '').replace('[count]', String(left));
    }

    validate() {
      if (!this.toggle?.checked) return true;
      this.clearErrors();
      const invalid = this.fields.filter((field) => !field.disabled && field.type !== 'hidden' && !field.checkValidity());
      invalid.forEach((field) => this.showError(field.dataset.recipientField, field.validationMessage));
      invalid[0]?.focus();
      return invalid.length === 0;
    }

    showErrors(errors) {
      const keys = { email: 'email', name: 'name', message: 'message', send_on: 'send_on', 'Recipient email': 'email', 'Recipient name': 'name', Message: 'message', 'Send on': 'send_on' };
      Object.entries(errors).forEach(([key, value]) => {
        const field = keys[key];
        if (field) this.showError(field, Array.isArray(value) ? value.join(', ') : String(value));
      });
    }

    showError(field, message) {
      const input = this.querySelector(`[data-recipient-field="${field}"]`);
      const error = this.querySelector(`[data-recipient-error="${field}"]`);
      if (!input || !error) return;
      error.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      const described = new Set((input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean));
      described.add(error.id);
      input.setAttribute('aria-describedby', Array.from(described).join(' '));
    }

    clearErrors() {
      this.querySelectorAll('[data-recipient-error]').forEach((error) => {
        error.hidden = true;
        error.textContent = '';
      });
      this.querySelectorAll('[aria-invalid]').forEach((input) => input.removeAttribute('aria-invalid'));
    }

    reset() {
      this.fields.forEach((field) => {
        if (field.type !== 'hidden') field.value = '';
      });
      this.toggle.checked = false;
      this.sync(false);
      this.updateCount();
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      const buttons = this.querySelectorAll('button');
      buttons[0]?.addEventListener('click', () => this.change(-1));
      buttons[1]?.addEventListener('click', () => this.change(1));
      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }

    get min() {
      return Number(this.input.min) || 1;
    }

    get step() {
      return Math.max(1, Number(this.input.step) || 1);
    }

    get max() {
      const max = Number(this.input.max);
      return max > 0 ? max : Infinity;
    }

    /* Snap to the quantity rule: min, increments of step, max. */
    clamp(value) {
      const raw = Number(value) || this.min;
      const stepped = this.min + Math.round((raw - this.min) / this.step) * this.step;
      return Math.min(this.max, Math.max(this.min, stepped));
    }

    change(delta) {
      this.input.value = this.clamp((Number(this.input.value) || this.min) + delta * this.step);
      this.sync();
    }

    sync() {
      const value = this.clamp(this.input.value);
      this.input.value = value;
      const [minus, plus] = this.querySelectorAll('button');
      if (minus) minus.disabled = value <= this.min;
      if (plus) plus.disabled = value >= this.max;
      this.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { quantity: value } }));
    }
  }

  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.target || '');
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
          this.dataset.position = entry.boundingClientRect.top < 0 ? 'above' : 'below';
        },
        { threshold: 0 }
      );
      this.observer.observe(target);
      this.bindHeaderOffset();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    bindHeaderOffset() {
      const header = document.querySelector('.section-header');
      const section = this.closest('.pdp-section');
      if (!header || !section) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        section.style.setProperty('--pdp-sticky-top', `${bottom}px`);
      };
      const request = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request);
      header.addEventListener('transitionend', request);
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = doc.querySelector('product-recommendations');
          if (next && next.innerHTML.trim()) {
            this.innerHTML = next.innerHTML;
            this.dispatchEvent(new CustomEvent('matter:reveal', { bubbles: true }));
          }
        } catch (error) {
          console.warn('Recommendations failed', error);
        }
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            load();
          },
          { rootMargin: '400px 0px' }
        );
        observer.observe(this);
      } else {
        load();
      }
    }
  }

  // Keep the buy button total (price × quantity) in sync.
  const syncTotals = (root) => {
    const totals = root.querySelectorAll('[data-buy-total]');
    if (!totals.length) return;
    const quantity = Math.max(1, Number(root.querySelector('quantity-input input')?.value) || 1);
    const format = document.documentElement.dataset.moneyFormat || root.dataset.moneyFormat || '{{amount}}';
    totals.forEach((total) => {
      const price = Number(total.dataset.price);
      if (!Number.isFinite(price)) return;
      const text = formatMoney(price * quantity, format);
      // Totals printed with money_without_trailing_zeros keep that style.
      total.textContent = total.hasAttribute('data-trim-zeros') ? text.replace(/[.,]00(?=\D*$)/, '') : text;
    });
  };
  document.addEventListener('quantity:change', (event) => syncTotals(event.target.closest('.pdp-section') || document));
  document.addEventListener('variant:swapped', (event) => syncTotals(event.target.closest('.pdp-section') || document));

  /* <delivery-estimate data-min data-max>: a range of working days from the
   * server ("2–4 zile lucrătoare") shown as calendar dates ("28 oct. – 4 nov."),
   * counted from today and skipping weekends and Romanian public holidays
   * (Labour Code art. 139: fixed days plus Orthodox Good Friday, Easter and
   * Pentecost). Any formatting error keeps the server text. */
  const holidayCache = new Map();
  const holidaysFor = (year) => {
    if (holidayCache.has(year)) return holidayCache.get(year);
    const days = new Set(['01-01', '01-02', '01-06', '01-07', '01-24', '05-01', '06-01', '08-15', '11-30', '12-01', '12-25', '12-26'].map((day) => `${year}-${day}`));
    // Julian Easter computus, shifted to the Gregorian calendar.
    const a = year % 4;
    const b = year % 7;
    const c = year % 19;
    const d = (19 * c + 15) % 30;
    const e = (2 * a + 4 * b - d + 34) % 7;
    const month = Math.floor((d + e + 114) / 31);
    const day = ((d + e + 114) % 31) + 1;
    const shift = Math.floor(year / 100) - Math.floor(year / 400) - 2;
    const easter = new Date(Date.UTC(year, month - 1, day + shift, 12));
    [-2, 0, 1, 49, 50].forEach((offset) => {
      const date = new Date(easter);
      date.setUTCDate(date.getUTCDate() + offset);
      days.add(date.toISOString().slice(0, 10));
    });
    holidayCache.set(year, days);
    return days;
  };
  const isWorkday = (date) => {
    const weekday = date.getUTCDay();
    return weekday !== 0 && weekday !== 6 && !holidaysFor(date.getUTCFullYear()).has(date.toISOString().slice(0, 10));
  };
  const addWorkdays = (start, count) => {
    const date = new Date(start);
    for (let added = 0; added < count; ) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isWorkday(date)) added += 1;
    }
    return date;
  };

  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min);
      const max = Number(this.dataset.max);
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= 0) return;
      const now = new Date();
      const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 12));
      try {
        const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const start = addWorkdays(today, Math.max(0, min));
        const end = addWorkdays(today, Math.max(min, max));
        this.title = this.textContent.trim();
        // formatRange folds a shared month ("23–27 oct."); older engines fall back.
        this.textContent = typeof format.formatRange === 'function' ? format.formatRange(start, end) : `${format.format(start)} – ${format.format(end)}`;
      } catch {
        /* keep the server text */
      }
    }
  }

  customElements.define('delivery-estimate', DeliveryEstimate);
  customElements.define('product-gallery', ProductGallery);
  customElements.define('variant-picker', VariantPicker);
  customElements.define('product-form', ProductForm);
  customElements.define('product-model', ProductModel);
  customElements.define('gift-card-recipient', GiftCardRecipient);
  customElements.define('product-tabs', ProductTabs);
  customElements.define('quantity-input', QuantityInput);
  customElements.define('sticky-buy-bar', StickyBuyBar);
  customElements.define('product-recommendations', ProductRecommendations);
}
